import { Color, Spacing } from '@persona-id/design-tokens';
import {
  BriefcaseIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  CircleMinusIcon,
  UsersIcon,
  UsersXIcon,
} from '@persona-id/design-tokens/icons';
import styled from 'styled-components';

import ReportRecurStatusSticker from 'dashboard/components/ReportRecurStatusSticker';
import { formatDate } from 'dashboard/lib/utils/formatters';
import {
  BUSINESS_REPORT_TYPES,
  Report,
  ReportStatus,
  SCREENING_REPORT_TYPES,
} from 'dashboard/models/report';
import { ReportTemplate } from 'dashboard/models/report-template';
import { Label } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';

const ModuleHeaderContainer = styled('div')`
  display: grid;
`;

const TitleContainer = styled('div')`
  display: flex;
  align-items: center;
`;

const Title = styled('span')`
  margin-right: ${Spacing[2]};
  grid-gap: ${Spacing[2]};
  display: flex;
  align-items: center;
`;

const SubTitle = styled('div')`
  ${paragraphMixins.md}

  color: ${({ theme }) => theme.color.text.tertiary};
  height: ${({ theme }) => theme.lineHeightSmall};
`;

const getTitleName = (reportType: ApiModel) => {
  switch (reportType) {
    case ApiModel.ReportAddressLookup:
      return 'Address Lookup Report';
    case ApiModel.ReportAdverseMedia:
      return 'Adverse Media Report';
    case ApiModel.ReportBusinessAdverseMedia:
      return 'Business Adverse Media Report';
    case ApiModel.ReportBusinessAssociatedPersons:
      return 'Business Associated Persons Report';
    case ApiModel.ReportBusinessClassification:
      return 'Business Classification Report';
    case ApiModel.ReportBusinessIndustryClassification:
      return 'Business Industry Classification Report';
    case ApiModel.ReportBusinessRegistrationsLookup:
      return 'Business Registrations Report';
    case ApiModel.ReportBusinessLiensLookup:
      return 'Business Liens Report';
    case ApiModel.ReportBusinessLookup:
      return 'Business Lookup Report';
    case ApiModel.ReportBusinessNonprofit:
      return 'Business Nonprofit Report';
    case ApiModel.ReportBusinessSocialMedia:
      return 'Business Social Media Report';
    case ApiModel.ReportBusinessWatchlist:
      return 'Business Watchlist Report';
    case ApiModel.ReportChainalysisAddressScreening:
      return 'Chainalysis Address Screening Report';
    case ApiModel.ReportCryptoAddressWatchlist:
      return 'Crypto Address Watchlist Report';
    case ApiModel.ReportCustomList:
      return 'Custom List Report';
    case ApiModel.ReportEmailAddress:
      return 'Email Risk Report';
    case ApiModel.ReportFinraBrokerCheck:
      return 'FINRA BrokerCheck Report';
    case ApiModel.ReportMxAccount:
      return 'Mx Account Report';
    case ApiModel.ReportPhoneNumber:
      return 'Phone Risk Report';
    case ApiModel.ReportPoliticallyExposedPerson:
      return 'Politically Exposed Person Report';
    case ApiModel.ReportProfile:
      return 'Profile Report';
    case ApiModel.ReportSecActionLookup:
      return 'SEC Action Lookup Report';
    case ApiModel.ReportSentilinkApplicationRisk:
      return 'SentiLink Application Risk Report';
    case ApiModel.ReportSentilinkScores:
      return 'SentiLink Scores Report';
    case ApiModel.ReportSocialMedia:
      return 'Social Media Report';
    case ApiModel.ReportSynthetic:
      return 'Synthetic Report';
    case ApiModel.ReportWatchlist: {
      return 'Watchlist Report';
    }
    default: {
      return 'Report';
    }
  }
};

const getReadyReportIcon = (report: Report) => {
  const hasMatch = report.attributes['has-match'];

  if (hasMatch == null) {
    return <CircleCheckIcon color={Color.BorderIconSuccess} width={20} height={20} />;
  }

  const isBusinessReport = BUSINESS_REPORT_TYPES.includes(report.type);
  const isScreeningReport = SCREENING_REPORT_TYPES.includes(report.type);

  // We show warning if either we have a match and it's a screening report (bad actor found),
  // or if we don't have a match and it's not a screening report (could not find enrichment).
  const showWarning = hasMatch === isScreeningReport;

  // For screening reports, the success color should be green.
  // For enrichment reports, they should be neutral.
  const successColor = isScreeningReport ? Color.BorderIconSuccess : Color.BorderIconPrimary;

  if (isBusinessReport) {
    return showWarning ? (
      <BriefcaseIcon color={Color.BorderIconWarning} width={20} height={20} />
    ) : (
      <BriefcaseIcon color={successColor} width={20} height={20} />
    );
  }

  return showWarning ? (
    <UsersXIcon color={Color.BorderIconWarning} width={20} height={20} />
  ) : (
    <UsersIcon color={successColor} width={20} height={20} />
  );
};

const getReportStatusIcon = (report: Report) => {
  switch (report.attributes.status) {
    case ReportStatus.Submitted:
    case ReportStatus.Pending:
      return <CircleMinusIcon color={Color.BorderIconWarning} width={20} height={20} />;
    case ReportStatus.Ready:
      return getReadyReportIcon(report);
    case ReportStatus.Errored:
    case ReportStatus.Restricted:
      return <CircleAlertIcon color={Color.BorderIconDanger} width={20} height={20} />;
    default:
      assertNever(report.attributes.status);
  }
};

interface ReportModuleHeaderProps {
  report: Report;
  reportTemplate: ReportTemplate | undefined;
}

const ReportModuleHeader = ({ report, reportTemplate }: ReportModuleHeaderProps) => (
  <ModuleHeaderContainer>
    <TitleContainer>
      <Title>
        {getReportStatusIcon(report)}
        {reportTemplate ? reportTemplate.attributes.name : getTitleName(report.type)}
      </Title>
      <ReportRecurStatusSticker report={report} verbose={true} />
    </TitleContainer>
    <SubTitle>{report.attributes['primary-input'] || ''}</SubTitle>
    <Label size='md' color='tertiary'>
      {`${report.id} | Created ${formatDate(report.attributes['created-at'])}`}
    </Label>
  </ModuleHeaderContainer>
);

export default ReportModuleHeader;
